import * as XLSX from 'xlsx'
import type {
  TrolleySheetData,
  ToolRow,
} from '@/utils/tool-management/tool-management.util'

export const EXCEL_MAX_SIZE = 10 * 1024 * 1024
export const EXCEL_ALLOWED_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

export interface ReadExcelOptions {
  onProgress?: (progress: number) => void
}

export interface ReadExcelResult {
  data: TrolleySheetData
}

export enum ExcelErrorCode {
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  FILE_SIZE_TOO_LARGE = 'FILE_SIZE_TOO_LARGE',
  EXCEL_READ_ERROR = 'EXCEL_READ_ERROR',
  FILE_READING_FAILED = 'FILE_READING_FAILED',
}

export class ExcelReadError extends Error {
  constructor(
    message: string,
    public readonly code: ExcelErrorCode,
    public readonly originalError?: unknown
  ) {
    super(message)
    this.name = 'ExcelReadError'
  }
}

export function validateExcelFile(file: File): {
  valid: boolean
  error?: ExcelErrorCode
} {
  if (!EXCEL_ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: ExcelErrorCode.INVALID_FILE_TYPE,
    }
  }

  if (file.size > EXCEL_MAX_SIZE) {
    return {
      valid: false,
      error: ExcelErrorCode.FILE_SIZE_TOO_LARGE,
    }
  }

  return { valid: true }
}

export function readExcelFile(
  file: File,
  options: ReadExcelOptions = {}
): Promise<ReadExcelResult> {
  const { onProgress } = options

  return new Promise<ReadExcelResult>((resolve, reject) => {
    const reader = new FileReader()

    reader.onloadstart = () => {
      onProgress?.(1)
    }

    reader.onprogress = event => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 99)
        onProgress?.(percent)
      }
    }

    reader.onload = e => {
      try {
        const data = e.target!.result
        const workbook = XLSX.read(data as ArrayBuffer, { type: 'array' })
        const allSheetsData: TrolleySheetData = {}

        workbook.SheetNames.forEach(sheetName => {
          const worksheet = workbook.Sheets[sheetName]
          const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
          allSheetsData[sheetName] = json as ToolRow[]
        })

        onProgress?.(100)
        resolve({ data: allSheetsData })
      } catch (error) {
        console.error('XLSX processing error:', error)
        reject(
          new ExcelReadError(
            'Error reading or parsing the Excel file. Please check the format.',
            ExcelErrorCode.EXCEL_READ_ERROR,
            error
          )
        )
      }
    }

    reader.onerror = error => {
      console.error('FileReader Error:', error)
      reject(
        new ExcelReadError(
          'File reading failed.',
          ExcelErrorCode.FILE_READING_FAILED,
          error
        )
      )
    }

    reader.readAsArrayBuffer(file)
  })
}
